import { ItemType, MapTo, ScopeStatistics, Workspace, SyncOptions } from "./ResearchDataModel"
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { groupBy } from "lodash"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { listToItemPermissions } from '@webapp/gp/Permissions'
import { ItemPermissions } from '@webapp/gp/GroupAdminModel'
import { useGroupStore } from './GroupStore'
import { useSyncStore } from './SyncStore'
import { PatchChange } from '@webapp/util/Api'

const workspaceExpand = "lastSync"

export const useWorkspaceStore = defineStore("workspaceStore", () => {
    const asyncWorkspaces = new Map<string, AsyncData<Workspace>>()
    const asyncWorkspaceKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncSyncKeys = new Map<string, AsyncData<VersionedKeyList>>()

    const rd = useResearchDataApi()
    const groupStore = useGroupStore()
    const syncStore = useSyncStore()

    function getAsyncWorkspace(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncWorkspaceList([id], loadMode)[0] : undefined
    }

    function getAsyncWorkspaceList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useWorkspaceStore() // use to get/return reactive store objects
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainByIdsAsync("workspaces", idsToLoad, workspaceExpand)
            return processExpandedWorkspaces(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncWorkspaces, ids, "Workspace", loadMode, loadAsync)
    }

    async function getWorkspaceListAsync(ids: (string | undefined)[]) {
        const asyncItems = getAsyncWorkspaceList(ids, LoadMode.EnsureLoaded)
        return (await Promise.all(asyncItems.map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    function getLoadedWorkspaces(ids: (string | undefined)[]) {
        const asyncItems = getAsyncWorkspaceList(ids, LoadMode.TrackOnly)
        return asyncItems.map(a => a.data).filter(isDefined)
    }

    function getAsyncKeyListsForGroups(groupIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        return getAsyncKeyListsForRelatedWorkspaces(groupIds, "Group", groupStore.asyncWorkspaceKeys, loadMode, 
            id => `groups/${id}/workspaces`, workspaceExpand)
    }

    function getAsyncKeyListForAll(loadMode = LoadMode.TrackOnly) {
        const store = useWorkspaceStore() // use to get/return reactive store objects
        const loadAsync = async (sourceKeys: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainCollectionAsync("workspaces", workspaceExpand)
            return processExpandedWorkspaces(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListForRelatedItems("all", "Root", store.asyncWorkspaceKeys,
            store.asyncWorkspaces, "Workspace", loadMode, loadAsync)!
    }

    function getAsyncWorkspacesForGroups(groupIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const asyncKeyLists = getAsyncKeyListsForGroups(groupIds, loadMode)
        const workspaceIds = asyncKeyLists.flatMap(a => a.data?.keys ?? [])
        return getAsyncWorkspaceList(workspaceIds)
    }

    function getAllAsyncWorkspaces(loadMode = LoadMode.TrackOnly) {
        return getAsyncWorkspaceList(getAsyncKeyListForAll(loadMode).data?.keys ?? [])
    }

    async function getAllWorkspacesAsync(loadMode = LoadMode.EnsureLoaded) {
        const keyList = await getAsyncKeyListForAll(loadMode).whenLoadCompleted
        if (!keyList)
            return []
        
        const allWorskpaces = getAsyncWorkspaceList(keyList!.keys).map(a => a.data).filter(isDefined) // should be loaded
        addWorkspaceKeyListsForAllGroupsToStore(allWorskpaces, keyList!.modifiedDate!)
        return allWorskpaces
    }

    function getWorkspacesForGroups(groupIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const keys = getAsyncKeyListsForGroups(groupIds, loadMode).flatMap(a => a.data?.keys ?? [])
        return getLoadedWorkspaces(keys)
    }
    
    async function getWorkspacesForGroupsAsync(groupIds: string[], loadMode = LoadMode.EnsureLoaded) {
        const keyListPromises = getAsyncKeyListsForGroups(groupIds, loadMode).map(a => a.whenLoadCompleted)
        const keyLists = await Promise.all(keyListPromises)
        const keys = keyLists.filter(isDefined).flatMap(a => a.keys)
        return getLoadedWorkspaces(keys)
    }

    async function getPermissionsAsync(id: string | undefined) {
        if (!id)
            return ItemPermissions.None
        const p = await rd.getPlainAsync(`workspaces/${id}/permissions`)
        return listToItemPermissions(p.toString())
    }

    async function getStatisticsAsync(ids: (string | undefined)[]) {
        const definedIds = ids.filter(isDefined)
        const list = await rd.getPlainByIdsAsync(id => `workspaces/${id}/statistics`, definedIds)
        return list.map(plainToStatistics)
    }

    function processExpandedWorkspaces(plainWorkspaces: any[], requestTime: DateTime) {
        syncStore.addSyncsToStore(plainWorkspaces.map(p => p.lastSync).filter(isDefined), requestTime)
        return AsyncDataStore.createItemMap(plainWorkspaces, "Workspace", plainToWorkspace, w => w.id!)
    }

    function getAsyncKeyListsForRelatedWorkspaces(sourceItemKeys: (string | undefined)[],
        sourceTypeName: string,
        relatedKeysStoreMap: Map<string, AsyncData<VersionedKeyList>>,
        loadMode: LoadMode,
        relatedItemsPathBuilder: (id: string) => string,
        expand?: string) {

        const definedSourceItemKeys = sourceItemKeys.filter(isDefined)
        if (!definedSourceItemKeys.length) {
            return []
        }

        const store = useWorkspaceStore() // use to get/return reactive store objects
        const loadAsync = async (sourceKeys: string[], requestTime: DateTime) => {
            // TODO: this API returns a flattened list of all workspaces for all groups, but getPlainByIdsAsync assumes
            // that the result should be an "item" (in this case a list) for each group, so when a list of N group ids is
            // passed, it returns an array of N workspace lists, when what we want is a flat list of all workspaces.
            // For now, just be sure we only call this with a single group id, and flatten the result.
            if (definedSourceItemKeys.length > 1)
                throw new Error("getRelatedWorkspaces only supports a single group id")
            
            // when getPlainByIdsAsync is provided a single id, it assumes a single item response, but this is really a 
            // related items request, so it will always return a list, and therefore need to use list expansion
            expand = rd.injectExpandItems(expand)

            const plainItemLists = await rd.getPlainByIdsAsync(relatedItemsPathBuilder, definedSourceItemKeys, expand)

            const logKey = definedSourceItemKeys.length == 1 ? definedSourceItemKeys[0] : `(${sourceItemKeys.length} keys)`
            console.log(`Loaded ${plainItemLists.length} workspace lists at ${relatedItemsPathBuilder(logKey)}`)

            const plainItems = plainItemLists.flat()
            return processExpandedWorkspaces(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListsForRelatedItems(sourceItemKeys, sourceTypeName, relatedKeysStoreMap,
            store.asyncWorkspaces, "Workspace", loadMode, loadAsync, w => w.viewGroupId!)
    }

    async function mapItemIdsAsync(workspaceId: string, itemType: ItemType, itemIds: string[], mapTo: MapTo) {
        const query = { type: ItemType[itemType], to: MapTo[mapTo] }
        const p = await rd.postPlainAsync(`workspaces/${workspaceId}/map`, itemIds, query)
        return p as string[]
    }

    function addWorkspaceKeyListsForAllGroupsToStore(allWorkspaces: Workspace[], requestTime: DateTime) {
        const workspacesByGroup = groupBy(allWorkspaces, w => w.viewGroupId)
        for (const [groupId, workspaces] of Object.entries(workspacesByGroup)) {
            const workspaceIds = workspaces.map(w => w.id!)
            AsyncDataStore.addKeyListToStore(groupStore.asyncWorkspaceKeys, groupId, "Group.WorkspaceKeys", workspaceIds, requestTime)
        }
    }

    async function addAsync(workspace: Workspace) {
        const store = useWorkspaceStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await rd.postPlainAsync(`workspaces`, workspace)
            return plainToWorkspace(p) // current user is owner on add
        }
        return await AsyncDataStore.addToStoreMapAsync(store.asyncWorkspaces, "Workspace", w => w.id, addCoreAsync)
    }

    async function patchAsync(id: string, changes: PatchChange[]) {
        const store = useWorkspaceStore() // use to get/return reactive store objects
        const patchCoreAsync = async (id: string, changes: PatchChange[]) => {
            const p = await rd.patchPlainAsync(`workspaces/${id}`, changes)
            return plainToWorkspace(p)
        }
        await AsyncDataStore.patchFromStoreAsync(store.asyncWorkspaces, id, "Workspace", changes, patchCoreAsync)
    }

    return {
        asyncWorkspaces,
        asyncWorkspaceKeys,
        asyncSyncKeys,

        getAsyncWorkspace,
        getAsyncWorkspaceList,
        getWorkspaceListAsync,
        getLoadedWorkspaces,
        getAsyncKeyListsForGroups,
        getAsyncKeyListForAll,
        getAsyncWorkspacesForGroups,
        getAllAsyncWorkspaces,
        getAllWorkspacesAsync,
        getWorkspacesForGroups,
        getWorkspacesForGroupsAsync,
        getPermissionsAsync,
        getStatisticsAsync,
        processExpandedWorkspaces,
        getRelatedWorkspaces: getAsyncKeyListsForRelatedWorkspaces,
        mapItemIdsAsync,
        
        addWorkspaceKeyListsForAllGroupsToStore,

        addAsync,
        patchAsync,
    }
})

function plainToWorkspace(p: any) {
    if (p.syncOptions) {
        p.syncOptions = assignExisting(new SyncOptions(), p.syncOptions)
    }
    const w = assignExisting(new Workspace(), p)
    w.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate);
    w.lastSyncId = p.lastSync?.id
    return w;
}

export function plainToStatistics(p: any) {
    const stats = <ScopeStatistics> p
    stats.personCount ??= 0
    stats.familyCount ??= 0
    stats.assertionCount ??= 0
    stats.placeCount ??= 0
    stats.siteCount ??= 0
    stats.contentCount ??= 0
    stats.contentTagCount ??= 0
    return stats
}
